/** Perspectiva de carriles — geometría unificada desde el punto de fuga. */

import { PERCUSION_CORPORAL, type PercusionBucketRect } from './percusionCorporal';

export const AR_LANE_TOP_Y = 18;
export const AR_VANISH_Y_RATIO = -0.22;

export interface PercusionViewport {
  width: number;
  horizonY: number;
}

export interface LaneLayout {
  lane: number;
  bottomX: number;
  bottomHalfW: number;
  /** Tinte pastel del carril (cristal esmerilado). */
  laneTint: string;
  xLeft: (y: number) => number;
  xRight: (y: number) => number;
  xCenter: (y: number) => number;
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

export function vanishPointX(vp: PercusionViewport): number {
  return vp.width * 0.5;
}

export function vanishPointY(vp: PercusionViewport): number {
  return vp.horizonY * AR_VANISH_Y_RATIO;
}

/** Rayo desde el punto de fuga hasta una esquina inferior del carril. */
export function laneEdgeX(edgeBottomX: number, y: number, vp: PercusionViewport): number {
  const vx = vanishPointX(vp);
  const vy = vanishPointY(vp);
  const hy = vp.horizonY;
  if (Math.abs(hy - vy) < 1) return edgeBottomX;
  const t = clamp((y - vy) / (hy - vy), 0, 1);
  return vx + (edgeBottomX - vx) * t;
}

/** Eje central del carril (misma recta que las esquinas). */
export function laneCenterX(bottomX: number, y: number, vp: PercusionViewport): number {
  return laneEdgeX(bottomX, y, vp);
}

export function buildLaneLayouts(
  bucketRects: PercusionBucketRect[],
  vp: PercusionViewport
): LaneLayout[] {
  return bucketRects.map((rect) => {
    const bottomHalfW = rect.r * 0.95;
    const leftBottom = rect.cx - bottomHalfW;
    const rightBottom = rect.cx + bottomHalfW;
    const laneTint = PERCUSION_CORPORAL[rect.lane]?.laneTint ?? 'rgba(248, 250, 252, 0.18)';

    return {
      lane: rect.lane,
      bottomX: rect.cx,
      bottomHalfW,
      laneTint,
      xLeft: (y: number) => laneEdgeX(leftBottom, y, vp),
      xRight: (y: number) => laneEdgeX(rightBottom, y, vp),
      xCenter: (y: number) => laneCenterX(rect.cx, y, vp),
    };
  });
}

/** Lookup rápido para burbujas — evita recomputar layout cada frame. */
export function bubbleLaneX(
  lane: number,
  y: number,
  layouts: LaneLayout[]
): number {
  return layouts[lane]?.xCenter(y) ?? 0;
}
